// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — useAutoLayout Hook (M3: V·IANet Flow)
// ELK-based auto-layout with animated transitions (Capa 2A)
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import type { Node } from '@xyflow/react';
import type { FlowNode, FlowEdge, FlowNodeData } from '../types/flowchart.types';
import { autoLayoutELK, type LayoutDirection } from '../editor/utils/elkLayout';

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAutoLayout() {
  const [isLayouting, setIsLayouting] = useState(false);

  const applyLayout = useCallback(
    async (
      nodes: FlowNode[],
      edges: FlowEdge[],
      setNodes: React.Dispatch<React.SetStateAction<FlowNode[]>>,
      direction?: LayoutDirection,
    ): Promise<void> => {
      if (nodes.length === 0) return;
      setIsLayouting(true);
      try {
        const layoutedNodes = await autoLayoutELK(nodes as Node<FlowNodeData>[], edges, direction);

        // Apply animated transition on each node
        const animated = (layoutedNodes as FlowNode[]).map((node) => ({
          ...node,
          style: {
            ...node.style,
            transition: 'transform 300ms ease-out',
          },
        }));

        setNodes(animated);

        // Remove transition style after animation completes
        setTimeout(() => {
          setNodes((nds) =>
            nds.map((n) => {
              if (!n.style?.transition) return n;
              const { transition: _, ...restStyle } = n.style;
              return { ...n, style: Object.keys(restStyle).length > 0 ? restStyle : undefined };
            }),
          );
        }, 350);
      } finally {
        setIsLayouting(false);
      }
    },
    [],
  );

  return { applyLayout, isLayouting };
}
